<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import trackingApiKeysApi from 'dashboard/api/pipelines/trackingApiKeys';

const store = useStore();
const { t } = useI18n();

const accountId = computed(() => store.getters.getCurrentAccountId);

const keys = ref([]);
const isLoading = ref(false);
const isCreating = ref(false);
const newLabel = ref('');
const labelError = ref('');
const visibleKeys = ref({});

const fetchKeys = async () => {
  isLoading.value = true;
  try {
    const { data } = await trackingApiKeysApi.getKeys(accountId.value);
    keys.value = data?.data || [];
  } catch {
    useAlert(t('TRACKING_API_KEYS.ERRORS.FETCH'));
  } finally {
    isLoading.value = false;
  }
};

const createKey = async () => {
  if (!newLabel.value.trim()) {
    labelError.value = t('TRACKING_API_KEYS.FORM.LABEL.ERROR');
    return;
  }
  labelError.value = '';
  isCreating.value = true;
  try {
    const { data } = await trackingApiKeysApi.createKey(accountId.value, {
      label: newLabel.value.trim(),
    });
    keys.value.push(data?.data ?? data);
    newLabel.value = '';
    visibleKeys.value[data.id] = true;
  } catch {
    useAlert(t('TRACKING_API_KEYS.ERRORS.CREATE'));
  } finally {
    isCreating.value = false;
  }
};

const deleteKey = async keyId => {
  if (!window.confirm(t('TRACKING_API_KEYS.DELETE.CONFIRM'))) return;
  try {
    await trackingApiKeysApi.deleteKey(accountId.value, keyId);
    keys.value = keys.value.filter(k => k.id !== keyId);
    delete visibleKeys.value[keyId];
  } catch {
    useAlert(t('TRACKING_API_KEYS.ERRORS.DELETE'));
  }
};

const toggleVisibility = keyId => {
  visibleKeys.value[keyId] = !visibleKeys.value[keyId];
};

const copyKey = async key => {
  try {
    await navigator.clipboard.writeText(key);
  } catch {
    // fallback silencioso
  }
};

const maskKey = key => `${key.slice(0, 8)}${'•'.repeat(24)}${key.slice(-8)}`;

onMounted(fetchKeys);
</script>

<template>
  <div class="mx-auto max-w-3xl p-6">
    <div class="mb-8">
      <h1 class="text-xl font-semibold text-slate-900 dark:text-slate-50">
        {{ t('TRACKING_API_KEYS.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {{ t('TRACKING_API_KEYS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Create form -->
    <div class="mb-8 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
      <h2 class="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
        {{ t('TRACKING_API_KEYS.FORM.TITLE') }}
      </h2>
      <div class="flex gap-3">
        <div class="flex-1">
          <input
            v-model="newLabel"
            type="text"
            :placeholder="t('TRACKING_API_KEYS.FORM.LABEL.PLACEHOLDER')"
            class="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:border-woot-500 focus:outline-none focus:ring-1 focus:ring-woot-500"
            @keyup.enter="createKey"
          />
          <p v-if="labelError" class="mt-1 text-xs text-red-500">
            {{ labelError }}
          </p>
        </div>
        <woot-button
          variant="solid"
          color="primary"
          size="small"
          :is-loading="isCreating"
          @click="createKey"
        >
          {{ t('TRACKING_API_KEYS.FORM.BUTTON') }}
        </woot-button>
      </div>
    </div>

    <!-- Keys list -->
    <div v-if="isLoading" class="flex justify-center py-12">
      <span class="i-lucide-loader-circle animate-spin text-2xl text-woot-500" />
    </div>

    <div v-else-if="keys.length === 0" class="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 py-12 text-center">
      <span class="i-lucide-key text-3xl text-slate-300 dark:text-slate-600" />
      <p class="mt-3 text-sm text-slate-500 dark:text-slate-400">
        {{ t('TRACKING_API_KEYS.EMPTY') }}
      </p>
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="k in keys"
        :key="k.id"
        class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-slate-800 dark:text-slate-200">
              {{ k.label || t('TRACKING_API_KEYS.UNLABELED') }}
            </p>
            <p class="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
              {{ t('TRACKING_API_KEYS.CREATED_AT') }}
              {{ new Date(k.created_at).toLocaleDateString('pt-BR') }}
            </p>
            <div class="mt-2 flex items-center gap-2">
              <code class="flex-1 truncate rounded bg-slate-100 dark:bg-slate-700 px-2 py-1 font-mono text-xs text-slate-700 dark:text-slate-300">
                {{ visibleKeys[k.id] ? k.key : maskKey(k.key) }}
              </code>
              <button
                class="flex-shrink-0 rounded p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                :title="visibleKeys[k.id] ? t('TRACKING_API_KEYS.HIDE') : t('TRACKING_API_KEYS.SHOW')"
                @click="toggleVisibility(k.id)"
              >
                <span :class="visibleKeys[k.id] ? 'i-lucide-eye-off' : 'i-lucide-eye'" class="text-base" />
              </button>
              <button
                class="flex-shrink-0 rounded p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                :title="t('TRACKING_API_KEYS.COPY')"
                @click="copyKey(k.key)"
              >
                <span class="i-lucide-copy text-base" />
              </button>
            </div>
          </div>
          <button
            class="flex-shrink-0 rounded p-1.5 text-slate-400 hover:text-red-500 transition-colors"
            :title="t('TRACKING_API_KEYS.DELETE.BUTTON')"
            @click="deleteKey(k.id)"
          >
            <span class="i-lucide-trash-2 text-base" />
          </button>
        </div>
      </div>
    </div>

    <!-- Docs link -->
    <div class="mt-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ t('TRACKING_API_KEYS.DOCS_HINT') }}
        <a
          href="/guide"
          target="_blank"
          class="text-woot-500 hover:underline font-medium"
        >
          {{ t('TRACKING_API_KEYS.DOCS_LINK') }}
        </a>
      </p>
    </div>
  </div>
</template>
